import React, { useState, useCallback, useMemo, useEffect } from 'react';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { asDateInTZ, asUTCDate } from '@/utilities/convertToISO';
import { css } from '@emotion/react';
import { genAttributes } from '@onward-delivery/core';
import { OnwardSwitch, SecondaryButton } from '@/styles/blocks';
import { Grid, MenuItem, TextField } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

import { ResponsiveSidebarDialog, StickyModalActions, ModalContent, ModalTitle, PrimaryButton } from '@/styles/blocks';

import { Header2, Header1, DateTimePicker, DateTimeRangePicker, Body1, Tag } from '../Crossdocking/blocks';
import PhoneNumberInput from '@/components/PhoneNumberInput';
import { toE164, toNational } from '@/utilities/formatPhoneNumber';
import { toMinuteIso } from '@/utilities/toMinuteIso';

const REQUIRED_FIELDS = {
    WAREHOUSE: ['est_ship_date', 'est_received_date', 'location_id'],
    CUSTOMER: ['est_dropoff_date_start', 'est_dropoff_date_end', 'est_pickup_date'],
};

const CrossdockConfigModal = ({ event, order, drivers, action, warehouses, stage, callbacks }) => {
    const { zip } = genAttributes(order);
    const [stageOverride, setCurrentStage] = useState(stage || 'WAREHOUSE');
    const [updates, setUpdates] = useState({});
    const [sendConfirm, setSendConfirm] = useState(false);
    const [tempWhDriver, setTempWhDriver] = useState(false);

    const currentStage = useMemo(() => {
        return stageOverride || stage || 'WAREHOUSE';
    }, [stageOverride, stage]);

    const defaults = useMemo(() => {
        return {
            est_pickup_date: order.preferred_delivery_date,
            est_dropoff_date_start: order.del_window_start || order.preferred_delivery_date,
            est_dropoff_date_end: order.del_window_end || order.preferred_delivery_date,
        };
    }, [order]);

    const updated = useMemo(() => {
        return {
            ...defaults,
            ...(event || {}),
            ...updates,
        };
    }, [updates, event, defaults]);

    const tz = useMemo(() => {
        return order[zip] ? zipcode_to_timezone.lookup(order[zip]) : 'America/New_York';
    }, [order, zip]);

    const isComplete = useMemo(() => {
        return !REQUIRED_FIELDS[currentStage].some((field) => {
            return !updated[field] || updated[field].length === 0;
        });
    }, [updated, currentStage]);

    useEffect(() => {
        if (!event) return;
        const existingWhDriver = event.wh_driver;
        if (!existingWhDriver) return;

        const isTeammate = !!drivers?.find((d) => d.teammate_id === existingWhDriver.driver_id);
        if (!isTeammate) {
            setTempWhDriver(true);
            setUpdates((prev) => ({
                ...prev,
                driver_name: existingWhDriver.name,
                driver_phone: existingWhDriver.phone,
            }));
        }
    }, [event, drivers]);

    const onClose = () => {
        setCurrentStage(null);
        callbacks.onClose();
    };

    const onRevert = useCallback(() => {
        if (currentStage === 'CUSTOMER') {
            setCurrentStage('WAREHOUSE');
        } else {
            setCurrentStage(null);
            callbacks.onClose();
        }
    }, [currentStage, callbacks]);

    const onAdvance = useCallback(() => {
        if (currentStage === 'WAREHOUSE') {
            setCurrentStage('CUSTOMER');
        } else {
            const adjustTZ = (ts) => {
                const utc = new Date(asUTCDate(ts).setUTCHours(0, 0, 0, 0)).toISOString();
                return asDateInTZ(utc, tz).toISOString();
            };

            const { est_dropoff_date_start, est_dropoff_date_end, ...rest } = { ...defaults, ...updates };
            let rv = rest;

            if (event.event_id) {
                rv = { ...rv, event_id: event.event_id, order_id: order.order_id, action: event.action };
            } else {
                rv = { ...rv, order_id: order.order_id, action };
            }

            setUpdates({});

            callbacks.onSubmit({
                event: rv,
                order: {
                    ...(rv.est_pickup_date ? { pickup_date: adjustTZ(rv.est_pickup_date) } : {}),
                    ...(rv.driver_id ? { driver_id: rv.driver_id } : {}),
                    ...(rv.wh_driver_id ? { wh_driver_id: rv.wh_driver_id } : {}),
                    ...(est_dropoff_date_start
                        ? {
                              del_window_start: toMinuteIso(est_dropoff_date_start),
                              delivery_date: adjustTZ(est_dropoff_date_start),
                          }
                        : {}),
                    ...(est_dropoff_date_end ? { del_window_end: toMinuteIso(est_dropoff_date_end) } : {}),
                },
                sendConfirm: sendConfirm,
            });
        }
    }, [currentStage, updates, event, callbacks, tz, defaults, sendConfirm, order]);

    const onSaveWarehouseLeg = useCallback(() => {
        if (currentStage !== 'WAREHOUSE') {
            return;
        }

        // Build event payload, preserving any existing fields on the event.
        const { driver_name, driver_phone, ...restUpdates } = updates;
        let rv = {
            ...(event || {}),
            ...restUpdates,
        };

        if (event && event.event_id) {
            rv = { ...rv, event_id: event.event_id, order_id: order.order_id, action: event.action };
        } else {
            rv = { ...rv, order_id: order.order_id, action };
        }

        if (tempWhDriver && (driver_name || driver_phone)) {
            rv = {
                ...rv,
                wh_driver: {
                    data: {
                        entity_id: order.order_id,
                        key: driver_phone || driver_name,
                        entity_type: 'ORDER',
                        name: driver_name || '',
                        phone: driver_phone || '',
                    },
                    on_conflict: {
                        constraint: 'drivers_order_key_uniq_idx',
                        update_columns: ['name', 'phone'],
                    },
                },
            };
        }

        // Order-level updates for warehouse leg
        const orderUpdates = {
            ...(rv.wh_driver_id ? { wh_driver_id: rv.wh_driver_id } : {}),
        };

        setUpdates({});
        callbacks.onSubmit({
            event: rv,
            order: orderUpdates,
            sendConfirm: false,
        });
        onClose();
    }, [currentStage, event, updates, order, action, callbacks, onClose]);

    let content;
    switch (currentStage) {
        case 'WAREHOUSE': {
            content = (
                <Grid direction="row" container>
                    <Grid
                        container
                        direction="column"
                        css={css`
                            flex: 1;
                            flex-basis: 0;
                        `}
                    >
                        <Grid
                            direction="row"
                            container
                            css={css`
                                margin-bottom: 12px;
                            `}
                        >
                            <Header1>Pickup</Header1>
                        </Grid>
                        <Grid
                            direction="row"
                            container
                            css={css`
                                margin-bottom: 20px;
                            `}
                        >
                            <DateTimePicker
                                tz={tz}
                                timestamp={updated?.est_ship_date}
                                onChange={(date) => {
                                    const corrected = date.toISOString();

                                    setUpdates((prev) => ({
                                        ...prev,
                                        est_ship_date: corrected,
                                    }));
                                }}
                            />
                        </Grid>
                        <Grid direction="row" container>
                            <Grid
                                container
                                direction="row"
                                css={css`
                                    margin-bottom: 12px;
                                `}
                            >
                                <OnwardSwitch
                                    label={<Header1>New Driver</Header1>}
                                    checked={tempWhDriver}
                                    onChange={() => setTempWhDriver((prev) => !prev)}
                                />
                            </Grid>
                            {tempWhDriver ? (
                                <>
                                    <TextField
                                        variant="outlined"
                                        label="Driver Name"
                                        InputLabelProps={{
                                            shrink: !!updates?.driver_name,
                                        }}
                                        value={updates?.driver_name || ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setUpdates((prev) => ({
                                                ...prev,
                                                driver_name: value,
                                            }));
                                        }}
                                        css={css`
                                            width: 240px;
                                        `}
                                    />
                                    <TextField
                                        variant="outlined"
                                        label="Driver Phone"
                                        InputProps={{
                                            inputComponent: PhoneNumberInput,
                                        }}
                                        InputLabelProps={{
                                            shrink: !!updates?.driver_phone,
                                        }}
                                        value={toNational(updates?.driver_phone || '')}
                                        onChange={(e) => {
                                            const value = toE164(e.target.value);
                                            setUpdates((prev) => ({
                                                ...prev,
                                                driver_phone: value,
                                            }));
                                        }}
                                        css={css`
                                            margin-left: 12px;
                                            width: 160px;
                                        `}
                                    />
                                </>
                            ) : (
                                <TextField
                                    select
                                    variant="outlined"
                                    label="Driver"
                                    value={updated?.wh_driver_id || ''}
                                    onChange={(e) => {
                                        setUpdates((prev) => ({
                                            ...prev,
                                            wh_driver_id: e.target.value,
                                        }));
                                    }}
                                    fullWidth
                                >
                                    {drivers?.map((option) => (
                                        <MenuItem key={option.teammate_id} value={option.teammate_id}>
                                            {option.username}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        direction="column"
                        css={css`
                            flex: 0;
                            flex-basis: 0;
                            justify-content: center;
                            margin: 0 20px;
                        `}
                    >
                        <ArrowForwardIcon />
                    </Grid>
                    <Grid
                        container
                        direction="column"
                        css={css`
                            flex: 1;
                            flex-basis: 0;
                        `}
                    >
                        <Grid
                            direction="row"
                            container
                            css={css`
                                margin-bottom: 12px;
                            `}
                        >
                            <Header1>Warehouse Dropoff</Header1>
                        </Grid>
                        <Grid
                            direction="row"
                            container
                            css={css`
                                margin-bottom: 20px;
                            `}
                        >
                            <DateTimePicker
                                tz={tz}
                                disableDate={!updated?.est_ship_date}
                                timestamp={updated?.est_received_date}
                                onChange={(date) => {
                                    const corrected = date.toISOString();

                                    setUpdates((prev) => ({
                                        ...prev,
                                        est_received_date: corrected,
                                    }));
                                }}
                            />
                        </Grid>
                        <Grid direction="row" container>
                            <TextField
                                select
                                variant="outlined"
                                label="Warehouse Dropoff"
                                value={updated?.location_id || ''}
                                onChange={(e) => {
                                    setUpdates((prev) => ({
                                        ...prev,
                                        location_id: e.target.value,
                                    }));
                                }}
                                fullWidth
                            >
                                {warehouses?.map((option) => (
                                    <MenuItem key={option.location_id} value={option.location_id}>
                                        {`${option.location_name} - ${option.business_address || option.address}`}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                </Grid>
            );

            break;
        }
        case 'CUSTOMER': {
            content = (
                <>
                    <Grid direction="row" container>
                        <Grid
                            container
                            direction="column"
                            css={css`
                                flex: 1;
                                flex-basis: 0;
                            `}
                        >
                            <Grid
                                direction="row"
                                container
                                css={css`
                                    margin-bottom: 12px;
                                `}
                            >
                                <Header1>Warehouse Pickup</Header1>
                            </Grid>
                            <Grid
                                direction="row"
                                container
                                css={css`
                                    margin-bottom: 20px;
                                `}
                            >
                                <DateTimePicker
                                    tz={tz}
                                    timestamp={updated?.est_pickup_date}
                                    onChange={(date) => {
                                        const corrected = date.toISOString();

                                        setUpdates((prev) => ({
                                            ...prev,
                                            est_pickup_date: corrected,
                                        }));
                                    }}
                                />
                            </Grid>
                            <Grid direction="row" container>
                                <TextField
                                    select
                                    variant="outlined"
                                    label="Driver"
                                    value={updated?.driver_id || ''}
                                    onChange={(e) => {
                                        setUpdates((prev) => ({
                                            ...prev,
                                            driver_id: e.target.value,
                                        }));
                                    }}
                                    fullWidth
                                >
                                    {drivers?.map((option) => (
                                        <MenuItem key={option.teammate_id} value={option.teammate_id}>
                                            {option.username}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                        <Grid
                            container
                            direction="column"
                            css={css`
                                flex: 0;
                                flex-basis: 0;
                                justify-content: center;
                                margin: 0 20px;
                            `}
                        >
                            <ArrowForwardIcon />
                        </Grid>
                        <Grid
                            container
                            direction="column"
                            css={css`
                                flex: 1;
                                flex-basis: 0;
                            `}
                        >
                            <Grid
                                direction="row"
                                container
                                css={css`
                                    margin-bottom: 12px;
                                `}
                            >
                                <Header1>Customer Dropoff</Header1>
                            </Grid>
                            <Grid
                                direction="row"
                                container
                                css={css`
                                    flex-wrap: nowrap;
                                `}
                            >
                                <DateTimeRangePicker
                                    tz={tz}
                                    range={[updated?.est_dropoff_date_start, updated?.est_dropoff_date_end]}
                                    onChange={([start, end]) => {
                                        setUpdates((prev) => ({
                                            ...prev,
                                            est_dropoff_date_start: start.toISOString(),
                                            est_dropoff_date_end: end.toISOString(),
                                        }));
                                    }}
                                />
                            </Grid>
                            <Grid
                                direction="row"
                                container
                                css={css`
                                    flex: 1;
                                    flex-basis: 0;
                                    justify-content: flex-start;
                                `}
                            >
                                <OnwardSwitch
                                    label={<Body1>Send customer text to confirm drop off date & time</Body1>}
                                    checked={sendConfirm}
                                    onChange={() => setSendConfirm((prev) => !prev)}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid direction="row" container>
                        <Tag
                            css={css`
                                width: 700px;
                                margin-top: 20px;
                            `}
                        >
                            <Body1>{`Customer Preferred Delivery Date: ${
                                order.preferred_delivery_date_formatted || 'N/A'
                            }`}</Body1>
                            <br />
                            <Body1>{`Customer Alternative Delivery Dates: ${
                                order.alternative_delivery_dates
                                    ? order.alternative_delivery_dates_formatted.join(', ')
                                    : 'N/A'
                            }`}</Body1>
                        </Tag>
                    </Grid>
                </>
            );

            break;
        }
    }

    return (
        <ResponsiveSidebarDialog open={!!event} onClose={onClose}>
            <ModalTitle onClose={onClose}>
                <Header2
                    css={css`
                        color: #4c4c4c;
                    `}
                >
                    {currentStage === 'WAREHOUSE'
                        ? 'Crossdock - Pickup to Warehouse'
                        : 'Crossdock - Warehouse to Customer'}
                </Header2>
            </ModalTitle>
            <ModalContent
                css={css`
                    width: 100%;
                `}
            >
                {content}
            </ModalContent>
            <StickyModalActions>
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 0;
                    `}
                >
                    <SecondaryButton onClick={onRevert}>
                        {currentStage === 'WAREHOUSE' ? 'Cancel' : 'Back'}
                    </SecondaryButton>
                </Grid>
                {currentStage === 'WAREHOUSE' && (
                    <Grid
                        container
                        direction="column"
                        css={css`
                            flex: 0;
                            margin: 0 8px;
                        `}
                    >
                        <SecondaryButton disabled={!isComplete} onClick={onSaveWarehouseLeg}>
                            Save &amp; Close
                        </SecondaryButton>
                    </Grid>
                )}
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 0;
                    `}
                >
                    <PrimaryButton disabled={!isComplete} onClick={onAdvance}>
                        {currentStage === 'WAREHOUSE' ? 'Next' : 'Done'}
                    </PrimaryButton>
                </Grid>
            </StickyModalActions>
        </ResponsiveSidebarDialog>
    );
};

export default CrossdockConfigModal;
